import { useState } from 'react';

import { WebhookGatewayMessage } from '../../../../../types/WebhookMessages';
import { DropdownMenu } from '../../../../cdl/DropdownMenu/DropdownMenu';
import { useToasts } from '../../../../cdl/Toast/useToasts';
import { translate } from '../../../../common/helpers/translate.helper';
import { useWebhookUpdate } from '../hooks/useWebhookUpdate';

import { EditWebhookModal } from './EditWebhookModal';
import { WebhookDeleteModal } from './WebhookDeleteModal';
import { WebhookDisableModal } from './WebhookDisableModal';

interface WebhookTableDropdownProps {
    webhook: WebhookGatewayMessage;
    groupId: string;
}

export const WebhookTableDropdown = ({ webhook, groupId }: WebhookTableDropdownProps) => {
    const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
    const [showDisableConfirmPopup, setShowDisableConfirmPopup] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const webhookUpdateMutation = useWebhookUpdate();
    const { addErrorToast } = useToasts();

    const handleEnableWebhook = () => {
        const webhookRequest = {
            name: webhook.name,
            url: webhook.url,
            groupId,
            customerIds: webhook.customerIds,
            resourceTypes: webhook.resourceTypes,
            enabled: true,
        };

        webhookUpdateMutation.mutate(
            { webhookId: webhook.id, webhook: webhookRequest },
            {
                onError: () => {
                    addErrorToast(translate('global.genericToastError'));
                },
            }
        );
    };

    const options = [
        {
            label: translate('webhooks.edit'),
            onSelect: () => setShowEditModal(true),
        },
        ...(webhook.enabled
            ? [
                  {
                      label: translate('webhooks.disable'),
                      onSelect: () => setShowDisableConfirmPopup(true),
                  },
              ]
            : [
                  {
                      label: translate('webhooks.enable'),
                      onSelect: handleEnableWebhook,
                  },
              ]),
        {
            label: translate('webhooks.delete'),
            onSelect: () => setShowDeleteConfirmPopup(true),
            destructive: true,
        },
    ];

    return (
        <>
            <DropdownMenu options={options} align="end" />
            <WebhookDeleteModal
                webhook={webhook}
                isOpen={showDeleteConfirmPopup}
                onDismiss={() => setShowDeleteConfirmPopup(false)}
            />
            <WebhookDisableModal
                webhook={webhook}
                groupId={groupId}
                isOpen={showDisableConfirmPopup}
                onDismiss={() => setShowDisableConfirmPopup(false)}
            />
            <EditWebhookModal
                webhook={webhook}
                groupId={groupId}
                isOpen={showEditModal}
                onDismiss={() => setShowEditModal(false)}
            />
        </>
    );
};
